import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import Layout, { PageHeader } from "../components/Layout";
import { api, formatApiError } from "../lib/api";
import { Search, ClipboardCheck, MapPin } from "lucide-react";
import { CATEGORY_LABELS, CATEGORY_ORDER } from "../lib/categories";
import FlavorQuickAdd, { FLAVOR_ADDABLE_CATEGORIES } from "../components/FlavorQuickAdd";
import { useAuth } from "../context/AuthContext";
import { unitText } from "../lib/stock";

const BLISTER_CATEGORIES = new Set(["macaron", "macaron_seasonal"]);
const FULL_TRAY = 70;

/**
 * Franchisee **Submit Daily Report** — physical inventory walk.
 *
 * The franchisee walks the store, types the TOTAL count for each item, and
 * submits. Backend `POST /inventory/recount` sets `current_stock` to that
 * absolute number (scoped to the franchisee's own location — Kitchen and
 * sibling stores are never touched).
 *
 * Macarons + seasonal macarons: Full (× 70) + Partial (0–70).
 * Every other category: single total count.
 *
 * Each category has an inline "+ Add missing flavor" row at the bottom so a
 * store can add a flavor they're carrying that isn't yet in the catalog.
 */
export default function DailyReport() {
  const { user } = useAuth();
  const locationName = user?.franchise_name || user?.location_name || "your store";
  const [items, setItems] = useState([]);
  const [query, setQuery] = useState("");
  const [loaded, setLoaded] = useState(false);
  const [count, setCount] = useState({}); // { [id]: number | {full, partial} }
  const [busy, setBusy] = useState(false);
  const [notes, setNotes] = useState("");
  const sectionRefs = useRef({});

  const refresh = () => api.get("/inventory")
    .then((r) => {
      // Dedup by id + hard-filter to this franchisee's own location so a
      // stale cache can never show cross-store flavors here.
      const seen = new Set();
      const scoped = r.data
        .filter((it) => !user?.location_id || it.location_id === user.location_id)
        .filter((it) => (seen.has(it.id) ? false : seen.add(it.id)));
      setItems(scoped);
      setLoaded(true);
    })
    .catch(() => setLoaded(true));

  useEffect(() => { refresh(); }, []);

  const filtered = useMemo(() => {
    const q = query.toLowerCase().trim();
    return items.filter((i) => !q || i.name.toLowerCase().includes(q));
  }, [items, query]);

  const grouped = useMemo(() => {
    const buckets = {};
    for (const it of filtered) (buckets[it.category] ||= []).push(it);
    const keys = [
      ...CATEGORY_ORDER.filter((k) => buckets[k]),
      ...Object.keys(buckets).filter((k) => !CATEGORY_ORDER.includes(k)),
    ];
    return keys.map((k) => ({
      key: k, label: CATEGORY_LABELS[k] || k,
      rows: buckets[k].sort((a, b) => a.name.localeCompare(b.name)),
    }));
  }, [filtered]);

  const totalOf = (entry, category) => {
    if (entry === "" || entry === undefined || entry === null) return null;
    if (typeof entry === "number") return entry;
    if (BLISTER_CATEGORIES.has(category)) {
      const f = Number(entry.full) || 0;
      const p = Number(entry.partial) || 0;
      if (f === 0 && p === 0 && !entry.touched) return null;
      return f * FULL_TRAY + p;
    }
    return Number(entry) || null;
  };

  const setSingle = (id, v) => {
    const n = v === "" ? "" : Math.max(0, parseInt(v, 10) || 0);
    setCount((p) => ({ ...p, [id]: n }));
  };
  const setBlister = (id, key, v) => {
    const n = v === "" ? "" : Math.max(0, parseInt(v, 10) || 0);
    setCount((p) => {
      const prev = typeof p[id] === "object" && p[id] !== null ? p[id] : { full: "", partial: "" };
      return { ...p, [id]: { ...prev, [key]: n, touched: true } };
    });
  };

  const lineCount = Object.entries(count).filter(([id, v]) => {
    const it = items.find((x) => x.id === id);
    return it && totalOf(v, it.category) !== null;
  }).length;

  const submit = async () => {
    const entries = [];
    for (const [item_id, v] of Object.entries(count)) {
      const it = items.find((x) => x.id === item_id);
      if (!it) continue;
      const total = totalOf(v, it.category);
      if (total === null) continue;
      entries.push({ item_id, quantity: total });
    }
    if (!entries.length) { toast.error("Enter a count for at least one item"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/inventory/recount", { entries, notes: notes || null });
      toast.success(`End-of-Day Count saved — ${data.updated.length} item${data.updated.length === 1 ? "" : "s"} updated`);
      setCount({});
      setNotes("");
      refresh();
    } catch (e) {
      toast.error("Couldn't save End-of-Day Count", { description: formatApiError(e.response?.data?.detail) || e.message });
    } finally { setBusy(false); }
  };

  const jumpTo = (k) => {
    const el = sectionRefs.current[k];
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <Layout>
      <PageHeader
        eyebrow="My Store · Daily Inventory Report"
        title="End-of-Day Inventory Count"
        description="Walk the store, count every item on hand, enter the total. What you type becomes your new on hand — your End-of-Day Count sets the count."
      />

      <div
        className="mb-5 flex items-center gap-3 rounded-xl border border-border bg-secondary/40 px-4 py-3 text-sm text-muted-foreground"
        data-testid="dr-location-note"
      >
        <MapPin className="h-4 w-4 text-foreground" />
        <span>
          Counting on-hand for <b className="text-foreground">{locationName}</b> only.
        </span>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search items…"
            data-testid="dr-search"
            className="h-11 pl-9 pr-3 rounded-xl border border-input bg-card text-sm w-72"
          />
        </div>
        <button
          onClick={submit}
          disabled={busy || lineCount === 0}
          data-testid="dr-save"
          className="h-12 px-6 rounded-xl bg-foreground text-background font-medium text-sm hover:bg-foreground/90 disabled:opacity-40 inline-flex items-center gap-2"
        >
          <ClipboardCheck className="h-4 w-4" />
          {busy ? "Saving…" : `Submit End-of-Day Count${lineCount > 0 ? ` · ${lineCount} item${lineCount > 1 ? "s" : ""}` : ""}`}
        </button>
      </div>

      <div className="flex gap-2 mb-6 flex-wrap" data-testid="dr-category-pills">
        {grouped.map((g) => (
          <button
            key={g.key}
            onClick={() => jumpTo(g.key)}
            data-testid={`dr-jump-${g.key}`}
            className="h-9 px-4 rounded-full text-xs font-medium bg-card border border-border text-muted-foreground hover:text-foreground hover:border-foreground/30"
          >
            {g.label} <span className="text-[10px] opacity-60">· {g.rows.length}</span>
          </button>
        ))}
      </div>

      <div className="space-y-8" data-testid="dr-groups">
        {loaded && grouped.length === 0 && (
          <div className="luxury-card p-12 text-center text-sm text-muted-foreground">No items match your search.</div>
        )}
        {grouped.map((g) => {
          const isBlister = BLISTER_CATEGORIES.has(g.key);
          return (
            <section
              key={g.key}
              ref={(el) => { sectionRefs.current[g.key] = el; }}
              data-testid={`dr-section-${g.key}`}
            >
              <h2 className="font-display text-xl font-medium tracking-tight mb-3 flex items-baseline gap-3">
                {g.label}
                <span className="text-xs font-normal text-muted-foreground uppercase tracking-wider">
                  {g.rows.length} item{g.rows.length !== 1 ? "s" : ""}
                </span>
              </h2>
              <div className="luxury-card overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-secondary/60">
                    <tr className="text-left">
                      <th className="px-5 py-3 label-eyebrow font-medium">Item</th>
                      <th className="px-5 py-3 label-eyebrow font-medium">Pack</th>
                      <th className="px-5 py-3 label-eyebrow font-medium text-right w-24">On Hand</th>
                      {isBlister ? (
                        <>
                          <th className="px-5 py-3 label-eyebrow font-medium text-right w-24">Full × 70</th>
                          <th className="px-5 py-3 label-eyebrow font-medium text-right w-28">Partial (0–70)</th>
                          <th className="px-5 py-3 label-eyebrow font-medium text-right w-24">= Count</th>
                        </>
                      ) : (
                        <th className="px-5 py-3 label-eyebrow font-medium text-right w-40">Count</th>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {g.rows.map((it) => (
                      <CountRow
                        key={it.id}
                        item={it}
                        isBlister={isBlister}
                        entry={count[it.id]}
                        onSingle={(v) => setSingle(it.id, v)}
                        onBlister={(k, v) => setBlister(it.id, k, v)}
                        blisterTotal={totalOf(count[it.id], g.key) || 0}
                      />
                    ))}
                  </tbody>
                </table>
              </div>
              {FLAVOR_ADDABLE_CATEGORIES.has(g.key) && (
                <FlavorQuickAdd
                  category={g.key}
                  categoryLabel={g.label}
                  onSaved={refresh}
                />
              )}
            </section>
          );
        })}
      </div>

      <div className="mt-6">
        <label className="label-eyebrow mb-2 block">Notes (optional)</label>
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={2}
          placeholder="Anything the manager should know?"
          data-testid="dr-notes"
          className="w-full rounded-xl border border-input bg-card px-3 py-2 text-sm"
        />
      </div>
    </Layout>
  );
}


function CountRow({ item, isBlister, entry, onSingle, onBlister, blisterTotal }) {
  return (
    <tr className="border-t border-border align-top" data-testid={`dr-row-${item.id}`}>
      <td className="px-5 py-3">
        <div className="font-medium">{item.name}</div>
        {item.flavor && item.flavor !== item.name && (
          <div className="text-xs text-muted-foreground mt-0.5">· {item.flavor}</div>
        )}
      </td>
      <td className="px-5 py-3 text-muted-foreground">
        {unitText(item)}
      </td>
      <td className="px-5 py-3 text-right text-muted-foreground tabular-nums" data-testid={`dr-onhand-${item.id}`}>
        {item.current_stock}
      </td>
      {isBlister ? (
        <>
          <td className="px-5 py-3 text-right">
            <input
              type="number" min="0" inputMode="numeric"
              value={entry && typeof entry === "object" ? (entry.full ?? "") : ""}
              onChange={(e) => onBlister("full", e.target.value)}
              placeholder="0"
              data-testid={`dr-full-${item.id}`}
              className="h-11 w-20 rounded-lg border border-input bg-card px-3 text-right text-sm tabular-nums focus:outline-none focus:ring-2 focus:ring-primary/40"
            />
          </td>
          <td className="px-5 py-3 text-right">
            <input
              type="number" min="0" max="70" inputMode="numeric"
              value={entry && typeof entry === "object" ? (entry.partial ?? "") : ""}
              onChange={(e) => {
                const raw = Math.max(0, Math.min(70, parseInt(e.target.value, 10) || 0));
                onBlister("partial", e.target.value === "" ? "" : String(raw));
              }}
              placeholder="0"
              data-testid={`dr-partial-${item.id}`}
              className="h-11 w-20 rounded-lg border border-input bg-card px-3 text-right text-sm tabular-nums focus:outline-none focus:ring-2 focus:ring-primary/40"
            />
          </td>
          <td className="px-5 py-3 text-right">
            <span className="font-display text-lg font-medium tabular-nums" data-testid={`dr-total-${item.id}`}>
              {blisterTotal}
            </span>
          </td>
        </>
      ) : (
        <td className="px-5 py-3 text-right">
          <input
            type="number" min="0" inputMode="numeric"
            value={typeof entry === "number" ? entry : (typeof entry === "string" ? entry : "")}
            onChange={(e) => onSingle(e.target.value)}
            placeholder={String(item.current_stock ?? 0)}
            data-testid={`dr-count-${item.id}`}
            className="h-11 w-24 rounded-lg border border-input bg-card px-3 text-right text-sm tabular-nums focus:outline-none focus:ring-2 focus:ring-primary/40"
          />
        </td>
      )}
    </tr>
  );
}

